import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../mendepartmentdetails/Sidebar';
import SubDeptImage from '../../../assets/image/department/smiling-businesswoman-shaking-hand-male-partner-group-meeting_1163-4637.jpg';

function EmployerPartnershipsResources() {
  const [Faq, setFaq] = useState(0); // first dropdown open by default
  const titleRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');

  useEffect(() => {
    if (titleRef.current) {
      const c = window.getComputedStyle(titleRef.current).color;
      setTitleColor(c);
    }
  }, []);

  // shared inline styles (match extended pages)
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.35, color: '#0f172a' };
  const valText = { fontSize: '1rem', lineHeight: 1.6, color: '#334155' };
  const overviewText = { color: titleColor || '#0f172a', fontSize: '1.05rem', lineHeight: 1.6, margin: '8px 0 16px' };

  const sections = [
    {
      Que: 'Talent Acquisition & Job Matching',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Targeted postings & events:', 'Promote openings to qualified jobseekers; schedule virtual and in-person hiring events.'],
            ['Screening & referrals:', 'Pre-screen candidates for required skills/credentials and refer top matches.'],
            ['Onsite interviews:', 'Coordinate employer interview days and same-day conditional offers when feasible.'],
          ].map(([k, v], i) => (
            <li key={`ta-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Training & Upskilling Partnerships',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Customized cohorts:', 'Co-design short programs aligned to your roles, tools, and shifts.'],
            ['Incumbent worker training:', 'Upskill current staff for advancement and backfill entry roles.'],
            ['Pre-hire bootcamps:', 'Hands-on training with employer input; graduates pipeline directly into openings.'],
          ].map(([k, v], i) => (
            <li key={`train-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Work-Based Learning Pipelines',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Internships & apprenticeships:', 'Structured earn-and-learn models with mentor support.'],
            ['On-the-job training (OJT):', 'Reimbursement opportunities where eligible to offset onboarding costs.'],
            ['Mentor toolkits:', 'Templates and checklists that make supervisor coaching repeatable.'],
          ].map(([k, v], i) => (
            <li key={`wbl-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Incentives & Funding Navigation',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['WIOA & tax credits:', 'Guidance on eligibility, documentation, and timelines.'],
            ['Equipment & space leverage:', 'Align lab resources or employer sites for cost-effective delivery.'],
            ['Grant collaboration:', 'Letters of support, data, and proposal inputs for workforce grants.'],
          ].map(([k, v], i) => (
            <li key={`fund-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Diversity, Equity & Inclusion',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Inclusive hiring:', 'Structured interviews, skills-first screening, and bias-aware practices.'],
            ['Accessibility & accommodations:', 'Plan for assistive tech, modified tasks, and flexible scheduling.'],
            ['Second-chance hiring:', 'Support for candidates with justice involvement and reentry pathways.'],
          ].map(([k, v], i) => (
            <li key={`dei-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Retention & Employee Supports',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Coaching & EAP links:', 'Performance coaching and referrals to employee assistance resources.'],
            ['Barrier reduction:', 'Transportation, childcare referrals, and starter tools/gear where available.'],
            ['Career ladders:', 'Micro-credentials and internal pathways to reduce turnover.'],
          ].map(([k, v], i) => (
            <li key={`ret-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'HR & Compliance Support',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Job descriptions:', 'Role profiles, competencies, and screening rubrics.'],
            ['Safety & onboarding:', 'OSHA refreshers, site orientations, and checklists.'],
            ['Policy templates:', 'Attendance, conduct, and evaluation forms to standardize practices.'],
          ].map(([k, v], i) => (
            <li key={`hr-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Rapid Response & Layoff Aversion',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Continuity planning:', 'Cross-training and staggered schedules to stabilize operations.'],
            ['Business services:', 'Connections to financing, consulting, and market diversification.'],
            ['Rapid response:', 'Coordinated services for affected workers during closures/downsizing.'],
          ].map(([k, v], i) => (
            <li key={`rr-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Data, KPIs & Feedback',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Hiring & retention metrics:', 'Track time-to-fill, 90-day retention, and advancement.'],
            ['Satisfaction & NPS:', 'Employer and participant feedback loops for quality improvement.'],
            ['Outcome reporting:', 'Clear dashboards for stakeholders and grant requirements.'],
          ].map(([k, v], i) => (
            <li key={`data-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <section className="department-details-section">
      <div className="container">
        <div className="row">

          {/* Sidebar */}
          <div className="col-12 col-lg-4 col-xl-4">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-lg-8">
            <div className="department-details-imgbox">
              <img src={SubDeptImage} className="img-fluid" alt="Employer Partnerships and Workforce Resources" />
            </div>

            <div className="department-details-content-box">
              <h4 ref={titleRef} className="department-details-title">Employer Partnerships and Workforce Resources</h4>

              {/* Overview (always visible, same color as title but smaller) */}
              <p style={overviewText}>
                Build strong hiring pipelines, customize training to your roles, and improve retention with wraparound
                supports—while we help navigate incentives, track outcomes, and continuously refine what works.
              </p>
            </div>

            {/* Accordion (expanded information) */}
            <div className="accordian-box">
              {sections.map((item, index) => (
                <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                  <div className="accordian-title">
                    <h5>{item.Que}</h5>
                    <i
                      className="fa-solid fa-angle-right"
                      onClick={() => setFaq(index)}
                      role="button"
                      aria-label={`Toggle ${item.Que}`}
                    />
                  </div>

                  {Faq === index && (
                    <div className="accordian-content" style={{ display: 'block' }}>
                      {item.Answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="department-details-policy-box">
              <h4>Our Commitment:</h4>
              <p>
                We align talent supply with real employer demand—reducing time-to-hire, boosting retention, and growing
                a resilient local workforce.
              </p>
            </div>

            <div>
              <Link to="/department/employment">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }} />
                Go Back
              </Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default EmployerPartnershipsResources;
